// components/mobile/MobileDrawer.jsx
// Drawer kiri versi mobile — dibuka via edge-swipe dari tepi kiri layar
// (listener di MobileNav) atau programatik. Isi v1: header akun (nama, role,
// logout), status sistem ringkas (tap → Logs bila boleh), Favorit (toggle via
// tekan-lama di daftar sini maupun grid Home), Terakhir dibuka, footer.
// Favorit: localStorage per-device (lihat favorites.js).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, LogOut, ChevronRight, Star, Clock, ShieldCheck, ShieldAlert, ShieldX,
} from 'lucide-react';
import { api, useAuth } from '../../context/AuthContext';
import { ROUTE_META } from '../../routesMeta';
import { visualFor } from '../dashboardVisual';
import { useFavorites } from './favorites';

function canSee(route, hasAccess) {
  return route.permission === null ? true : hasAccess(route.permission);
}

// Tekan-lama (≈500ms) untuk toggle favorit. Mengembalikan handler touch.
// Digagalkan bila jari bergeser (dianggap scroll), dan klik normal tetap jalan.
function useLongPress(onLong, ms = 500) {
  const timer = useRef(null);
  const moved = useRef(false);
  const start = () => {
    moved.current = false;
    timer.current = setTimeout(() => { if (!moved.current) onLong(); }, ms);
  };
  const cancel = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
  return {
    onTouchStart: start,
    onTouchMove: () => { moved.current = true; cancel(); },
    onTouchEnd: cancel,
    onContextMenu: (e) => e.preventDefault(), // cegah context-menu bawaan saat long-press
  };
}

export default function MobileDrawer({ open, peek = 0, hold = false, onClose }) {
  const nav = useNavigate();
  const { user, hasAccess, logout } = useAuth();
  const [health, setHealth] = useState(null);
  const [favs, toggleFav] = useFavorites();
  const [recent, setRecent] = useState([]);
  const [hint, setHint] = useState(null); // label yang baru di-toggle (feedback singkat)

  const routes = ROUTE_META.filter(r => r.key !== 'overview' && canSee(r, hasAccess));
  const byKey = Object.fromEntries(routes.map(r => [r.key, r]));
  const favRoutes = favs.map(k => byKey[k]).filter(Boolean);

  // Health — fetch saat drawer dibuka (ringan, sekali per buka)
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get('/health/deep')
      .then(({ data }) => { if (alive) setHealth(data); })
      .catch(() => { if (alive) setHealth(false); });
    return () => { alive = false; };
  }, [open]);

  // Recent — sumber sama dgn MobileHome/Overview desktop
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/logs', {
          params: { action: 'VIEW_DASHBOARD', username: user?.username, limit: 40 },
        });
        if (cancelled) return;
        const seen = new Set(); const out = [];
        for (const log of (data?.logs || [])) {
          const label = String(log.detail || '').replace(/^Membuka:?\s*/i, '').trim();
          if (!label) continue;
          const route = routes.find(r => r.label.toLowerCase() === label.toLowerCase());
          if (!route || seen.has(route.key)) continue;
          seen.add(route.key); out.push(route);
          if (out.length >= 4) break;
        }
        setRecent(out);
      } catch { if (!cancelled) setRecent([]); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-line
  }, [open, user]);

  const go = (path) => { onClose(); nav(path); };
  const doLogout = () => { onClose(); logout(); };
  // Exit = keluar aplikasi. Catatan platform: browser hanya mengizinkan
  // window.close() utk tab yang dibuka via script / PWA standalone. Di tab
  // biasa Chrome akan diabaikan → fallback: mundur habis di history, terakhir
  // ganti halaman ke about:blank (upaya terbaik yang diizinkan web).
  const doExit = () => {
    onClose();
    try { window.close(); } catch {}
    setTimeout(() => {
      try {
        if (window.history.length > 1) window.history.go(-(window.history.length - 1));
        setTimeout(() => { try { window.location.replace('about:blank'); } catch {} }, 250);
      } catch {}
    }, 300);
  };
  const flashHint = (label, added) => {
    setHint(`${added ? '⭐ Added' : 'Removed'} : ${label}`);
    setTimeout(() => setHint(null), 1500);
  };

  // ── Swipe kanan→kiri di dalam drawer → tutup. Panel ikut jari (translateX
  // negatif), lepas > 70px → tutup; kurang → membal balik. Gerakan vertikal
  // dominan diserahkan ke scroll daftar.
  const [dragX, setDragX] = useState(0);
  const swipe = useRef({ x: null, y: null, active: false, cur: 0 });
  const onPanelTouchStart = (e) => {
    const t = e.touches[0];
    swipe.current = { x: t.clientX, y: t.clientY, active: false, cur: 0 };
  };
  const onPanelTouchMove = (e) => {
    const s = swipe.current;
    if (s.x == null) return;
    const t = e.touches[0];
    const dx = t.clientX - s.x, dy = Math.abs(t.clientY - s.y);
    if (!s.active) {
      if (dx < -14 && Math.abs(dx) > dy) s.active = true; // kiri & horizontal dominan
      else if (dy > 24) { s.x = null; return; }            // scroll vertikal
      else return;
    }
    s.cur = Math.min(0, dx);
    setDragX(s.cur);
  };
  const onPanelTouchEnd = () => {
    const s = swipe.current;
    if (s.active && s.cur < -70) { setDragX(0); swipe.current = { x: null, y: null, active: false, cur: 0 }; onClose(); return; }
    swipe.current = { x: null, y: null, active: false, cur: 0 };
    setDragX(0);
  };

  if (!open && peek <= 0 && !hold) return null;

  const roleLabel = user?.role === 'admin' ? 'Administrator' : (user?.role || 'User');
  // Posisi panel: saat gesture buka (belum open) → mengintip mengikuti jari
  // (calc(-100% + peek)); saat open → 0 + dragX (gesture tutup). Transition
  // aktif hanya saat jari lepas (settle) — saat diikuti jari harus 1:1 tanpa lag.
  // `hold` menahan mount sesaat setelah peek batal agar transisi balik terlihat.
  const following = (!open && peek > 0 && !hold) || (open && dragX !== 0);
  const panelTransform = open ? `translateX(${dragX}px)` : `translateX(calc(-100% + ${peek}px))`;
  const backdropOpacity = open ? 1 : Math.min(peek / 260, 1);

  return (
    <div className="fixed inset-0 z-[900] md:hidden" role="dialog" aria-modal="true"
         style={{ pointerEvents: open ? 'auto' : 'none' }}>
      <div className="absolute inset-0 bg-slate-900/40" onClick={open ? onClose : undefined}
           style={{ opacity: backdropOpacity, transition: following ? 'none' : 'opacity 0.2s ease' }} />
      <div className="absolute inset-y-0 left-0 w-[82vw] max-w-[320px] bg-white shadow-2xl flex flex-col"
           onTouchStart={onPanelTouchStart} onTouchMove={onPanelTouchMove} onTouchEnd={onPanelTouchEnd}
           style={{
             paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)',
             transform: panelTransform,
             transition: following ? 'none' : 'transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
             pointerEvents: 'auto',
           }}>

        {/* ── Header akun ── */}
        <div className="-mt-[env(safe-area-inset-top)] px-4 pb-5 flex items-start gap-3 text-white" style={{ paddingTop: 'calc(18px + env(safe-area-inset-top))', background: 'linear-gradient(160deg,#2e0854 0%,#4c1287 55%,#6d28d9 100%)' }}>
          <span className="w-12 h-12 rounded-full bg-white/20 ring-2 ring-white/30 text-white flex items-center justify-center font-bold text-lg shrink-0">
            {(user?.fullName || user?.username || '?').slice(0, 1).toUpperCase()}
          </span>
          <div className="flex-1 min-w-0">
            <p className="mt-1 font-bold text-white text-[16px] leading-tight truncate">{user?.fullName || user?.username}</p>
            <p className="text-xs text-white/70">{roleLabel}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-1.5 -mr-1 text-white/70"><X style={{ width: 18, height: 18 }} /></button>
        </div>

        {/* ── Konten scrollable ── */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          <HealthRow health={health} onOpen={hasAccess('logs') ? () => go('/dashboard/logs') : null} />

          {/* Terakhir dibuka (di atas Favorit) */}
          {recent.length > 0 && <>
            <SectionTitle icon={Clock} label="Recently Visited" />
            {recent.map(r => (
              <DrawerRow key={r.key} route={r} starred={favs.includes(r.key)}
                onClick={() => go(r.path)}
                onLong={() => { const added = !favs.includes(r.key); toggleFav(r.key); flashHint(r.label, added); }} />
            ))}
          </>}

          {/* Favorit */}
          <SectionTitle icon={Star} label="Favorites" />
          {favRoutes.length === 0 ? (
            <p className="px-2 pb-2 text-xs text-slate-400 leading-relaxed">
              None yet. <b>Long-press</b> an item above (or a tile on Home) to add one.
            </p>
          ) : favRoutes.map(r => (
            <DrawerRow key={r.key} route={r} starred
              onClick={() => go(r.path)}
              onLong={() => { toggleFav(r.key); flashHint(r.label, false); }} />
          ))}
        </div>

        {/* ── Footer: Exit (tutup drawer) + Logout ── */}
        <div className="border-t border-slate-100 px-3 py-2">
          {hint && <p className="px-2 pb-1 text-xs text-blue-600">{hint}</p>}
          <div className="flex gap-2">
            <button onClick={doExit}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm text-slate-600 border border-slate-200 active:bg-slate-50 font-medium">
              <X style={{ width: 16, height: 16 }} /> Exit
            </button>
            <button onClick={doLogout}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-xl text-sm text-rose-600 bg-rose-50 active:bg-rose-100 font-medium">
              <LogOut style={{ width: 16, height: 16 }} /> Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, label }) {
  return (
    <p className="px-2 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
      {Icon && <Icon style={{ width: 12, height: 12 }} />} {label}
    </p>
  );
}

function DrawerRow({ route, starred, onClick, onLong }) {
  const v = visualFor(route.key);
  const Icon = v.icon;
  const lp = useLongPress(onLong);
  return (
    <button onClick={onClick} {...lp}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-700 active:bg-slate-50 select-none"
      style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
      <span className={`w-8 h-8 rounded-lg ${v.bgColor} flex items-center justify-center shrink-0`}>
        <Icon className={v.color} style={{ width: 16, height: 16 }} />
      </span>
      <span className="flex-1 text-left truncate">{route.label}</span>
      {starred && <Star style={{ width: 14, height: 14 }} className="text-amber-400 fill-amber-400 shrink-0" />}
      <ChevronRight style={{ width: 16, height: 16 }} className="text-slate-300 shrink-0" />
    </button>
  );
}

function HealthRow({ health, onOpen }) {
  let tone, Icon, title;
  if (health === null)       { tone = 'slate';   Icon = ShieldCheck; title = 'Checking status…'; }
  else if (health === false) { tone = 'slate';   Icon = ShieldAlert; title = 'Status unavailable'; }
  else if (health.status === 'ok')   { tone = 'emerald'; Icon = ShieldCheck; title = 'All systems normal'; }
  else if (health.status === 'down') { tone = 'rose';    Icon = ShieldX;     title = 'Some systems down'; }
  else                               { tone = 'amber';   Icon = ShieldAlert; title = 'Partially degraded'; }
  const TONES = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber:   'bg-amber-50 border-amber-200 text-amber-700',
    rose:    'bg-rose-50 border-rose-200 text-rose-700',
    slate:   'bg-slate-50 border-slate-200 text-slate-500',
  };
  return (
    <button onClick={onOpen || undefined} disabled={!onOpen}
      className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left ${TONES[tone]} ${onOpen ? 'active:opacity-80' : 'cursor-default'}`}>
      <Icon style={{ width: 18, height: 18 }} className="shrink-0" />
      <span className="flex-1 text-[13px] font-medium leading-tight">{title}</span>
      {onOpen && <ChevronRight style={{ width: 14, height: 14 }} className="opacity-50 shrink-0" />}
    </button>
  );
}
